import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { userAPI } from '../services/api';
import './Settings.css';

const Settings = () => {
  const { user, refreshUser, isAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const targetUserId = searchParams.get('userId');
  const [targetUser, setTargetUser] = useState(null);
  const [loadingTargetUser, setLoadingTargetUser] = useState(false);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    defaultSport: 'Tennis (singles)',
    maxActiveLeagues: 1,
    canViewProfiles: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (targetUserId && isAdmin) {
      // Admin viewing another user's settings
      loadTargetUser();
    } else if (user) {
      // User viewing their own settings
      setFormData({
        name: user.name || '',
        defaultSport: user.defaultSport || 'Tennis (singles)',
        maxActiveLeagues: user.maxActiveLeagues ?? 1,
        canViewProfiles: user.canViewProfiles ?? true,
      });
    }
  }, [user, targetUserId, isAdmin]);

  const loadTargetUser = async () => {
    if (!targetUserId) return;
    setLoadingTargetUser(true);
    try {
      const response = await userAPI.getUser(targetUserId);
      const target = response.data;
      setTargetUser(target);
      setFormData({
        name: target.name || '',
        defaultSport: target.defaultSport || 'Tennis (singles)',
        maxActiveLeagues: target.maxActiveLeagues ?? 1,
        canViewProfiles: target.canViewProfiles ?? true,
      });
    } catch (err) {
      console.error('Failed to load target user:', err);
    } finally {
      setLoadingTargetUser(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const userIdToUpdate = targetUserId && isAdmin ? targetUserId : user.userId;
      const updateData = {
        name: formData.name,
        defaultSport: formData.defaultSport,
      };
      // Only include admin-controlled fields if admin is editing
      if (isAdmin) {
        if (formData.maxActiveLeagues !== undefined) {
          updateData.maxActiveLeagues = formData.maxActiveLeagues;
        }
        if (formData.canViewProfiles !== undefined) {
          updateData.canViewProfiles = formData.canViewProfiles;
        }
      }
      await userAPI.updateUser(userIdToUpdate, updateData);
      if (targetUserId && isAdmin) {
        await loadTargetUser();
        setSuccess('User settings updated successfully');
      } else {
        await refreshUser();
        setSuccess('Settings updated successfully');
      }
      setEditing(false);
    } catch (err) {
      console.error('Update error:', err);
      setError(err.response?.data?.error || 'Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  const displayUser = targetUserId && isAdmin ? targetUser : user;

  if (!user || (targetUserId && isAdmin && loadingTargetUser)) {
    return <div className="loading">Loading...</div>;
  }

  if (targetUserId && isAdmin && !targetUser) {
    return (
      <div className="page-container">
        <div className="container">
          <div className="error-message">User not found</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">Settings</h1>
          <p className="page-subtitle">
            {targetUserId && isAdmin 
              ? `Managing settings for ${displayUser?.name || 'user'}` 
              : 'Manage your account information'}
          </p>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="settings-grid">
          {/* Profile Information */}
          <div className="card">
            <div className="card-header">
              <h2>Profile Information</h2>
              {!editing && (!targetUserId || isAdmin) && (
                <button onClick={() => setEditing(true)} className="btn btn-secondary">
                  Edit
                </button>
              )}
            </div>

            {editing ? (
              <form onSubmit={handleSubmit} className="settings-form">
                <div className="form-group">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    name="name"
                    className="form-input"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-input"
                    value={displayUser?.email || ''}
                    disabled
                  />
                  <small className="form-helper">Email cannot be changed</small>
                </div>


                <div className="form-group">
                  <label className="form-label">Default Sport</label>
                  <select
                    name="defaultSport"
                    className="form-input sport-select"
                    value={formData.defaultSport}
                    onChange={handleChange}
                  >
                    <option value="Tennis (singles)">🎾 Tennis (singles)</option>
                    <option value="Tennis (doubles)" disabled>🎾 Tennis (doubles) (Coming Soon)</option>
                    <option value="Padel" disabled>🏓 Padel (Coming Soon)</option>
                    <option value="Squash">🏸 Squash</option>
                    <option value="Football" disabled>⚽ Football (Coming Soon)</option>
                    <option value="Basketball" disabled>🏀 Basketball (Coming Soon)</option>
                  </select>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing(false)}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className="settings-info">
                <div className="info-item">
                  <span className="info-label">Name</span>
                  <span className="info-value">{displayUser?.name || ''}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Email</span>
                  <span className="info-value">{displayUser?.email || ''}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Default Sport</span>
                  <span className="info-value sport-badge">
                    {(displayUser?.defaultSport === 'Tennis (singles)' || displayUser?.defaultSport === 'Tennis (doubles)') && '🎾'}
                    {displayUser?.defaultSport === 'Padel' && '🏓'}
                    {displayUser?.defaultSport === 'Squash' && '🏸'}
                    {displayUser?.defaultSport === 'Football' && '⚽'}
                    {displayUser?.defaultSport === 'Basketball' && '🏀'}
                    {' '}{displayUser?.defaultSport || 'Tennis (singles)'}
                  </span>
                </div>
              </div>
            )}
          </div>


          {/* Permissions Card - Visible to all, editable by admin only */}
          <div className="card">
            <div className="card-header">
              <h2>Permissions</h2>
              {isAdmin && !editing && (
                <button onClick={() => setEditing(true)} className="btn btn-secondary">
                  Edit
                </button>
              )}
            </div>
            {editing && isAdmin ? (
              <form onSubmit={handleSubmit} className="settings-form">
                <div className="form-group">
                  <label className="form-label">Max Active Leagues</label>
                  <input
                    type="number"
                    name="maxActiveLeagues"
                    className="form-input"
                    value={formData.maxActiveLeagues ?? (displayUser?.maxActiveLeagues ?? 1)}
                    onChange={(e) => setFormData({ ...formData, maxActiveLeagues: parseInt(e.target.value, 10) || 0 })}
                    min="0"
                    required
                  />
                  <small className="form-helper">Maximum number of active leagues/superleagues this user can have (default: 1, set to 0 to prevent league creation)</small>
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      name="canViewProfiles"
                      checked={formData.canViewProfiles ?? (displayUser?.canViewProfiles ?? true)}
                      onChange={(e) => setFormData({ ...formData, canViewProfiles: e.target.checked })}
                      style={{ width: '18px', height: '18px' }}
                    />
                    Can View Other User Profiles
                  </label>
                  <small className="form-helper">Allow this user to view other users' profile pages</small>
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing(false)}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className="settings-info">
                <div className="info-item">
                  <span className="info-label">Max Active Leagues</span>
                  <span className="info-value">{displayUser?.maxActiveLeagues ?? 1}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Can View Other User Profiles</span>
                  <span className="info-value">{displayUser?.canViewProfiles ?? true ? 'Yes' : 'No'}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;


